import React from 'react';
import { CURRENT_ESTIMATE_FEATURES, UPCOMING_ESTIMATE_FEATURES } from 'containers/estimates/constants';
import { EstimateCrownIcon, GreenFilledTickIcon, RedLockWithKeyHoleIcon, StarIcon } from 'assets/icons';
import { CustomButton } from 'components';
import { ButtonType } from 'components/custom-button/CustomButton';
import { useAppSelector } from 'hooks';
import { selectCurrentTeam } from 'containers/app/appSlice';
import { TeamRole } from 'containers/app/app.model';
import { VoidFn } from 'types/baseTypes';
import { getAmountWithCurrency } from 'utils/amountUtils';

interface EstimateModalProps {
  className?: string;
  onTryFree: VoidFn;
  trialDuration: number;
  price: number;
  isLoading: boolean;
}

const EstimateModal: React.FC<EstimateModalProps> = ({ onTryFree, trialDuration, price, isLoading }) => {
  const { role } = useAppSelector(selectCurrentTeam);

  const isOwner = role === TeamRole.OWNER;

  const handleChatWithUsClick = () => {
    window.fcWidget.open();
  };

  return (
    <div
      className="customTransparentScroll max-h-[700px] w-[90%] 
      max-w-[580px] overflow-auto rounded-md bg-primary p-4 sm:w-[580px] sm:p-6">
      <div className="mb-4">
        {isOwner ? (
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-secondaryBtn">
            <EstimateCrownIcon />
          </div>
        ) : (
          <RedLockWithKeyHoleIcon className="mx-auto" />
        )}
      </div>
      <div className="mb-2 text-center text-2xl font-bold text-heading">
        {isOwner
          ? `Unlock estimates for just ${getAmountWithCurrency(price / 100)}/month`
          : 'Estimates needs a subscription'}
      </div>
      <div className="mb-6 text-center text-17px font-semibold text-primaryText">
        {isOwner
          ? ' Start your free trial and discover powerful features that bring more value'
          : `Please reach out to your business owner to enable the Estimates feature.
           Estimates is available for ${getAmountWithCurrency(price / 100)}/month`}
      </div>
      <div className="mb-6 rounded-md border border-borderGray p-4 text-left text-sbase text-primaryText">
        <div className="mb-4 font-semibold">{isOwner ? 'Whats included today' : 'Feature highlights'}</div>
        <ul className="list-none space-y-4">
          {CURRENT_ESTIMATE_FEATURES.map((feature, index) => (
            <li key={index} className="flex items-center gap-2 font-medium">
              <GreenFilledTickIcon />
              {feature}
            </li>
          ))}
        </ul>
        {isOwner && (
          <>
            <div className="mt-9 mb-4 font-semibold">Coming up</div>
            <ul className="list-none space-y-4">
              {UPCOMING_ESTIMATE_FEATURES.map((feature, index) => (
                <li key={index} className="flex items-center gap-2 font-medium">
                  <StarIcon />
                  {feature}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
      {isOwner && (
        <CustomButton
          className="mb-4 w-full"
          id="try-free-for-15-days"
          type={ButtonType.PRIMARY}
          onClick={onTryFree}
          isLoading={isLoading}>
          Try free for {trialDuration} day{trialDuration > 1 ? 's' : ''}
        </CustomButton>
      )}
      <div className="text-center text-sbase font-medium text-accentText">
        Need help?{' '}
        <a onClick={handleChatWithUsClick} className="cursor-pointer font-bold text-secondary">
          Chat with us
        </a>
      </div>
    </div>
  );
};

export default EstimateModal;
